import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AuthFormIntro } from '@/components/auth/shell/auth-form-intro';
import { AuthSplitShell } from '@/components/auth/shell/auth-split-shell';
import { AuthStepper } from '@/components/auth/shell/auth-stepper';
import { CopilotStageCard } from '@/components/auth/onboarding-copilot/stage-card/copilot-stage-card';
import { DemoIntroDialog } from '@/components/auth/organization/demo-intro-dialog';
import { useOrganizationCopilot } from '@/components/auth/organization/organization-copilot/use-organization-copilot';
import { StepOrganization } from '@/components/auth/organization/step-organization';
import { TrialCard } from '@/components/billing/trial-card';
import { ROUTES } from '@/constants/routes';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { useBillingSummary } from '@/hooks/data/use-billing';
import { loadNamespaces } from '@/lib/i18n/i18n';

export const Route = createFileRoute('/_authenticated/organization-setup')({
  loader: () => loadNamespaces('auth', 'onboarding'),
  staticData: { title: 'route.organization_setup.title', hideAppShell: true },
  component: OrganizationSetupRoute,
});

function OrganizationSetupRoute(): React.ReactElement {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { data: user } = useQuery(meQueryOptions);
  const { data: summary } = useBillingSummary();
  const copilot = useOrganizationCopilot();

  const org = user?.organization;
  const steps = [
    { key: 'organization', label: t('stepper.organization_label') },
    { key: 'connect', label: t('stepper.connect_label') },
  ] as const;

  const goToConnect = (): void => {
    void navigate({ to: ROUTES.CONNECT_PLATFORMS });
  };

  return (
    <AuthSplitShell
      topBar={
        <AuthStepper steps={steps.map((step) => step.label)} current={0} />
      }
      stage={<CopilotStageCard {...copilot.card} />}
    >
      {org && (
        <DemoIntroDialog
          key={`demo-${org.id}`}
          demoMode={org.demo_mode}
          organizationId={org.id}
        />
      )}
      <AuthFormIntro title={t('organization_setup.form_title')} />
      {summary && <TrialCard summary={summary} />}
      {org && (
        <StepOrganization
          key={`form-${org.id}`}
          organization={org}
          onSuccess={goToConnect}
          onFieldFocus={copilot.onFieldFocus}
          onSnapshotChange={copilot.onSnapshotChange}
        />
      )}
    </AuthSplitShell>
  );
}
